import { BaseService } from './baseService';
import { Client } from '../types/clients';
import { Document } from '../types/documents';

export interface Loan {
  id: string;
  clientId: string;
  loanNumber: string;
  type: 'Term Loan' | 'Line of Credit' | 'Equipment Financing' | 'SBA Loan' | 'Merchant Cash Advance' | 'Invoice Factoring' | 'Real Estate Loan';
  purpose: 'Working Capital' | 'Equipment Purchase' | 'Real Estate' | 'Inventory' | 'Business Expansion' | 'Debt Consolidation' | 'Other';
  requestedAmount: number;
  approvedAmount?: number;
  outstandingBalance?: number;
  interestRate?: number;
  termMonths: number;
  paymentFrequency?: 'Weekly' | 'Bi-weekly' | 'Monthly' | 'Quarterly';
  paymentAmount?: number;
  collateral?: {
    type: 'Real Estate' | 'Equipment' | 'Inventory' | 'Accounts Receivable' | 'Personal Guarantee' | 'None';
    description?: string;
    value?: number;
  };
  status: 'Application' | 'Under Review' | 'Additional Info Required' | 'Approved' | 'Funded' | 'Current' | 'Past Due' | 'Default' | 'Paid Off' | 'Charged Off' | 'Denied';
  applicationDate: string;
  approvalDate?: string;
  fundingDate?: string;
  maturityDate?: string;
  nextPaymentDate?: string;
  documents?: Array<Document>;
  underwritingNotes?: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  assignedTo?: string;
  client?: Client;
}

export interface CreateLoanRequest {
  clientId: string;
  type: Loan['type'];
  purpose: Loan['purpose'];
  requestedAmount: number;
  termMonths: number;
  collateral?: Loan['collateral'];
  underwritingNotes?: string;
}

export interface UpdateLoanRequest {
  type?: Loan['type'];
  purpose?: Loan['purpose'];
  requestedAmount?: number;
  approvedAmount?: number;
  interestRate?: number;
  termMonths?: number;
  paymentFrequency?: Loan['paymentFrequency'];
  paymentAmount?: number;
  collateral?: Loan['collateral'];
  status?: Loan['status'];
  approvalDate?: string;
  fundingDate?: string;
  maturityDate?: string;
  nextPaymentDate?: string;
  underwritingNotes?: string;
  assignedTo?: string;
}

export interface UpdateLoanStatusRequest {
  status: Loan['status'];
  notes?: string;
  conditions?: string[];
}

export interface LoanFilters {
  clientId?: string;
  status?: string;
  loanType?: string;
  amountMin?: number;
  amountMax?: number;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface LoanListResponse {
  loans: Loan[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    hasMore: boolean;
  };
}

class LoansService extends BaseService {
  private readonly baseUrl = '/loans';

  async createLoan(loanData: CreateLoanRequest): Promise<Loan> {
    const response = await this.api.post(this.baseUrl, loanData);
    return response.data.loan;
  }

  async getLoan(id: string): Promise<Loan> {
    const response = await this.api.get(`${this.baseUrl}/${id}`);
    return response.data.loan;
  }

  async updateLoan(id: string, updates: UpdateLoanRequest): Promise<Loan> {
    const response = await this.api.patch(`${this.baseUrl}/${id}`, updates);
    return response.data.loan;
  }

  async updateLoanStatus(id: string, statusUpdate: UpdateLoanStatusRequest): Promise<Loan> {
    const response = await this.api.patch(`${this.baseUrl}/${id}/status`, statusUpdate);
    return response.data.loan;
  }

  async listLoans(filters?: LoanFilters): Promise<LoanListResponse> {
    const params = new URLSearchParams();
    
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString());
        }
      });
    }

    const response = await this.api.get(`${this.baseUrl}?${params.toString()}`);
    return {
      loans: response.data.loans,
      pagination: response.data.pagination
    };
  }

  async deleteLoan(id: string): Promise<void> {
    await this.api.delete(`${this.baseUrl}/${id}`);
  }

  // Utility methods for loan status workflow
  getStatusColor(status: Loan['status']): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' {
    switch (status) {
      case 'Application':
        return 'info';
      case 'Under Review':
        return 'primary';
      case 'Additional Info Required':
        return 'warning';
      case 'Approved':
        return 'success';
      case 'Funded':
        return 'success';
      case 'Current':
        return 'success';
      case 'Past Due':
        return 'warning';
      case 'Default':
        return 'error';
      case 'Paid Off':
        return 'secondary';
      case 'Charged Off':
        return 'error';
      case 'Denied':
        return 'error';
      default:
        return 'default';
    }
  }

  getNextPossibleStatuses(currentStatus: Loan['status']): Loan['status'][] {
    switch (currentStatus) {
      case 'Application':
        return ['Under Review', 'Additional Info Required', 'Denied'];
      case 'Under Review':
        return ['Additional Info Required', 'Approved', 'Denied'];
      case 'Additional Info Required':
        return ['Under Review', 'Approved', 'Denied'];
      case 'Approved':
        return ['Funded', 'Denied'];
      case 'Funded':
        return ['Current'];
      case 'Current':
        return ['Past Due', 'Paid Off'];
      case 'Past Due':
        return ['Current', 'Default'];
      case 'Default':
        return ['Charged Off'];
      case 'Paid Off':
      case 'Charged Off':
      case 'Denied':
        return []; // Terminal states
      default:
        return [];
    }
  }

  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  }

  calculateMonthlyPayment(principal: number, annualRate: number, termMonths: number): number {
    if (annualRate === 0) return principal / termMonths;
    
    const monthlyRate = annualRate / 100 / 12;
    const factor = Math.pow(1 + monthlyRate, termMonths);
    return (principal * monthlyRate * factor) / (factor - 1);
  }
}

export const loansService = new LoansService();
export default loansService;
